import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, ContextStore, QueryRoute, RouteStore } from 'bernie-core';
import { Layer } from 'bernie-view';

import { FormInputs, FormInputsProvider, FormQueryVariables, useFetchRatesAction } from '@shared-ui/lodging-form';
import { useBexApi } from '@shared-ui/bex-api-context';
import { useLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { SearchCriteriaModel } from 'stores/models';

import { DayName, UitkDatePickerProps, UitkNewDatePicker } from 'uitk-react-date-picker';
import { UitkLinkButton } from 'uitk-react-button';

import { useFormInputs } from 'components/infosite/shared-ui/form/use-form-inputs';

interface DateFieldProps {
  rfrr?: string;
  onChange?: (event, isTriggeredByStepper?: boolean) => void;
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  context?: ContextStore;
}

export type CustomTriggerDateFieldProps = DateFieldProps & UitkDatePickerProps;

const isValidDate = (date?: Date | undefined): boolean => Boolean(date && date.toDateString() !== 'Invalid Date');

const DateField: React.FC<CustomTriggerDateFieldProps> = inject(
  'routeStore',
  'analytics',
  'currentHotel'
)(props => {
  const { buttonText, analytics, rfrr, routeStore, triggeredSelector, currentHotel } = props;
  const { formatDateString, formatDate, getWeekData } = useLocalization();

  const { offerSearchData } = currentHotel;

  const startDate = offerSearchData?.startDate;
  const endDate = offerSearchData?.endDate;
  const startDateFormatted = startDate && formatDateString(startDate, { skeleton: 'MMMEd' });
  const endDateFormatted = endDate && formatDateString(endDate, { skeleton: 'MMMEd' });
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  const checkInDate = startDate && new Date(startDate);
  const checkOutDate = endDate && new Date(endDate);

  const [rates, disclaimer, fetchRates] = useFetchRatesAction(isValidDate(checkInDate) && isValidDate(checkOutDate));

  const open = () => {
    setShowDatePicker(true);
    if (isValidDate(checkInDate)) {
      fetchRates(checkInDate);
    }
    routeStore.updateSearch({ pwaDialog: `datepicker-${triggeredSelector}` });

    if (typeof analytics?.trackEvent === 'function' && rfrr) {
      analytics.trackEvent(rfrr, 'Open Date Picker', analytics.omnitureData);
    }
  };

  const save = (start, end) => {
    props.onChange({
      target: [
        {
          name: 'startDate',
          value: formatDate(start, { raw: 'yyyy-MM-dd' }),
        },
        {
          name: 'endDate',
          value: formatDate(end, { raw: 'yyyy-MM-dd' }),
        },
      ],
    });
  };

  const dismiss = () => {
    setShowDatePicker(false);
    props.routeStore.goBack();
  };

  const datePickerAnalytics = {
    closeAnalytics: analytics?.setupTracker(':ChangeDatesClose', 'Close Date Picker', null),
    doneAnalytics: analytics?.setupTracker(':ChangeDatesDone', 'Apply Date Picker', null),
  };

  const routerLinkWrapper = children => <>{children}</>;

  const firstDayOfWeek = parseInt(DayName[getWeekData().firstDay.toUpperCase()], 10); // No mount callback

  const dateFieldProps = {
    routerLinkWrapper,
    showDatePicker,
    useCustomTrigger: true,
  };

  const datePickerProps = {
    analytics: datePickerAnalytics,
    dateFieldProps,
    datePickerContent: rates,
    footerText: disclaimer,

    onDismissCB: dismiss,
    onSubmitCB: save,

    buttonText,
    startDate,
    endDate,
    doubleMonths: true,
    daysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
    firstDayOfWeek,
    midnightBookingOffset: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
    triggeredSelector,
  };

  return (
    <>
      <UitkLinkButton onClick={open}>{triggeredSelector === 'start' ? startDateFormatted : endDateFormatted}</UitkLinkButton>
      {showDatePicker && (
        <QueryRoute query={{ pwaDialog: `datepicker-${triggeredSelector}` }}>
          <Layer id={`home-datepicker-${triggeredSelector}`} onDismiss={dismiss}>
            <UitkNewDatePicker {...datePickerProps} />
          </Layer>
        </QueryRoute>
      )}
    </>
  );
});

DateField.defaultProps = {
  startDate: '',
  endDate: '',
};

export const CustomTriggerDateFieldWrapper: React.FC<CustomTriggerDateFieldProps> = inject('currentHotel')(props => {
  const { currentHotel } = props;
  const { context } = useBexApi();

  const { hotelId, searchCriteria } = currentHotel;
  const inputs = useFormInputs(hotelId, searchCriteria);

  const variable: FormQueryVariables = {
    ...((inputs || {}) as FormInputs),
    context,
  };

  if (!hotelId || !searchCriteria) {
    return null;
  }

  return (
    <FormInputsProvider value={variable}>
      <DateField {...props} />
    </FormInputsProvider>
  );
});

CustomTriggerDateFieldWrapper.displayName = 'CustomTriggerDateFieldWrapper';
